import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GraduationCap, Loader2, Plus, Trash2, TriangleAlert } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NativeSelect } from '../components/ui/native-select';
import { Card, CardContent } from '../components/ui/card';

const DEFAULT_START = new Date(new Date().setFullYear(new Date().getFullYear() - 1))
  .toISOString().slice(0, 10);
const DEFAULT_END = new Date().toISOString().slice(0, 10);

// ── Spec helpers ──────────────────────────────────────────────────────────────

/** An operand is addressed in the form as `slug` or `slug::key`. */
function operandKey(operand) {
  if (!operand || operand.attr === undefined) return '';
  return operand.key ? `${operand.attr}::${operand.key}` : operand.attr;
}

function parseOperandKey(value) {
  const [attr, key] = value.split('::');
  return { attr, key: key || null };
}

const SLUG_RE = /^[a-z][a-z0-9_]{0,31}$/;   // mirrors _SLUG_RE in spec.py

/**
 * The name an attribute gets when it is added — `sma50` for SMA(period=50).
 *
 * Prefers `period` over whichever numeric param happens to sort first, so
 * BollingerBands(period=20, num_std=2.0) reads as `bollingerbands20` rather
 * than being named after the standard deviation.
 */
function suggestSlug(alias, params, existing) {
  const numeric = Object.keys(params).sort()
    .map((k) => params[k])
    .find((v) => typeof v === 'number' && Number.isFinite(v));
  const marker = typeof params.period === 'number' ? params.period : numeric;

  let base = alias.toLowerCase().replace(/[^a-z0-9_]/g, '');
  if (typeof marker === 'number' && Number.isFinite(marker)) {
    base += String(marker).replace('.', '_').replace('-', 'neg');
  }
  base = base.slice(0, 28) || 'attr';
  if (!/^[a-z]/.test(base)) base = `a${base}`;
  let slug = base;
  let n = 2;
  while (existing.includes(slug)) { slug = `${base}_${n}`; n += 1; }
  return slug;
}

/** Rename an attribute, carrying every reference to it along. */
function renameSlug(spec, from, to) {
  const attrs = {};
  Object.keys(spec.attrs).forEach((k) => {
    attrs[k === from ? to : k] = spec.attrs[k];
  });
  const move = (operand) => (
    operand && operand.attr === from ? { ...operand, attr: to } : operand
  );
  return {
    ...spec,
    attrs,
    groups: spec.groups.map((g) => ({
      ...g,
      conditions: g.conditions.map((c) => ({
        ...c, left: move(c.left), right: move(c.right),
      })),
    })),
    score: {
      ...spec.score,
      ...(spec.score.of ? { of: move(spec.score.of) } : {}),
      ...(spec.score.num ? { num: move(spec.score.num) } : {}),
      ...(spec.score.den ? { den: move(spec.score.den) } : {}),
    },
  };
}

/** Default params for a newly added attribute — settable scalars only. */
function defaultParams(attr, settableTypes) {
  const out = {};
  (attr.params || []).forEach((p) => {
    if (!settableTypes.includes(p.type)) return;
    if (p.default !== null && p.default !== undefined) out[p.name] = p.default;
    else if (p.type === 'int') out[p.name] = 14;
    else if (p.type === 'float') out[p.name] = 1.0;
    else if (p.type === 'bool') out[p.name] = false;
  });
  return out;
}

function emptySpec() {
  return {
    spec_version: 1,
    attrs: {},
    groups: [],
    score: { mode: 'weighted_groups' },
  };
}

// ── Attribute palette ─────────────────────────────────────────────────────────

function AddAttribute({ catalog, schema, spec, onAdd }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Dismiss on click-outside and Escape. Without this the results stay open
  // over the attribute list below until something is picked.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const q = search.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    const out = [];
    catalog.forEach((cat) => {
      cat.attributes.forEach((attr) => {
        const shape = schema.shapes?.[attr.alias];
        // An attribute with no usable value (the categorical one) cannot take
        // part in a condition, so it is not offered.
        const usable = shape && (shape.shape
          || Object.values(shape.keys || {}).some(Boolean));
        if (!usable) return;
        if (attr.alias.toLowerCase().includes(q)
            || (attr.description || '').toLowerCase().includes(q)
            || cat.name.toLowerCase().includes(q)) {
          out.push({ ...attr, category: cat.name });
        }
      });
    });
    return out.slice(0, 40);
  }, [catalog, schema, q]);

  const existing = Object.keys(spec.attrs);

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex gap-2">
        <Input
          placeholder="Search attributes to add — RSI, debt, revenue growth…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="text-xs h-8"
        />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg">
          {matches.map((attr) => (
            <button
              key={attr.alias}
              type="button"
              onClick={() => {
                const params = defaultParams(attr, schema.settable_param_types || []);
                onAdd(suggestSlug(attr.alias, params, existing), attr.alias, params);
                setSearch('');
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 hover:bg-accent flex items-start gap-2"
            >
              <span className="text-[11px] font-mono text-foreground shrink-0">{attr.alias}</span>
              <span className="text-[10px] text-muted-foreground leading-tight flex-1 truncate">
                {attr.description}
              </span>
              <span className="text-[9px] text-muted-foreground/60 shrink-0">{attr.category}</span>
            </button>
          ))}
        </div>
      )}
      {open && q && matches.length === 0 && (
        <p className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover px-3 py-2 text-[11px] text-muted-foreground shadow-lg">
          Nothing matches “{search}”.
        </p>
      )}
    </div>
  );
}

function AttrRow({
  slug, decl, attr, schema,
  onChangeParam, onCommitParams, onRename, onRemove, usedBy, taken,
}) {
  const settable = schema.settable_param_types || [];
  const params = (attr?.params || []).filter((p) => settable.includes(p.type));

  // The name is what conditions, the chart legend and the signal metadata all
  // show, so it is the author's to set. It follows the parameters until they
  // change it; after that it is left alone.
  const [draft, setDraft] = useState(slug);
  useEffect(() => { setDraft(slug); }, [slug]);

  // Renaming changes this row's React key, which remounts it and destroys the
  // input being typed into. So the name is only resettled once editing is
  // finished — on focus we note whether it was still the suggested one, and we
  // act on that when focus leaves the row entirely.
  const wasSuggested = useRef(false);
  const edited = useRef(false);

  const noteParamFocus = () => {
    const others = taken.filter((t) => t !== slug);
    wasSuggested.current = slug === suggestSlug(decl.alias, decl.params, others);
  };

  const handleRowBlur = (e) => {
    // Tabbing between this row's own fields is not finishing.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (!edited.current) return;
    edited.current = false;
    if (wasSuggested.current) onCommitParams(slug);
  };

  const cleaned = draft.trim().toLowerCase();
  const valid = SLUG_RE.test(cleaned)
    && (cleaned === slug || !taken.includes(cleaned));

  const commit = () => {
    if (cleaned === slug) { setDraft(slug); return; }
    if (!valid) { setDraft(slug); return; }
    onRename(slug, cleaned);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border/50 last:border-0"
      onBlur={handleRowBlur}
    >
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setDraft(slug); e.currentTarget.blur(); }
        }}
        spellCheck={false}
        title="The name this attribute goes by in conditions and signal metadata"
        className={`h-7 w-28 shrink-0 text-[11px] font-mono text-primary ${
          valid ? '' : 'border-destructive focus-visible:ring-destructive'
        }`}
      />
      <span className="text-[11px] font-mono text-muted-foreground">{decl.alias}</span>
      {params.map((p) => (
        <label key={p.name} className="flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground">{p.name}</span>
          {p.type === 'bool' ? (
            <input
              type="checkbox"
              checked={Boolean(decl.params?.[p.name])}
              onFocus={noteParamFocus}
              onChange={(e) => {
                edited.current = true;
                onChangeParam(slug, p.name, e.target.checked);
              }}
            />
          ) : (
            <Input
              type="number"
              step={p.type === 'float' ? 'any' : '1'}
              value={decl.params?.[p.name] ?? ''}
              onFocus={noteParamFocus}
              onChange={(e) => {
                edited.current = true;
                onChangeParam(
                  slug, p.name,
                  e.target.value === '' ? '' : Number(e.target.value),
                );
              }}
              className="h-7 w-20 text-xs"
            />
          )}
        </label>
      ))}
      <span className="ml-auto flex items-center gap-2">
        {usedBy === 0 && (
          <span className="text-[10px] text-amber-400" title="Every attribute has to be used by a condition or the score">
            unused
          </span>
        )}
        <button
          type="button"
          onClick={() => onRemove(slug)}
          className="text-muted-foreground hover:text-destructive"
          title="Remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}

// ── Conditions ────────────────────────────────────────────────────────────────

/** Every addressable operand in the spec, with the shape that decides its operators. */
function useOperands(spec, schema) {
  return useMemo(() => {
    const out = [];
    Object.keys(spec.attrs).sort().forEach((slug) => {
      const alias = spec.attrs[slug].alias;
      const shape = schema.shapes?.[alias];
      if (!shape) return;
      if (shape.shape) {
        out.push({ value: slug, label: slug, shape: shape.shape });
      }
      Object.keys(shape.keys || {}).forEach((key) => {
        if (!shape.keys[key]) return;
        out.push({
          value: `${slug}::${key}`,
          label: `${slug}.${key}`,
          shape: shape.keys[key],
        });
      });
    });
    return out;
  }, [spec.attrs, schema]);
}

function ConditionRow({ cond, operands, schema, onChange, onRemove }) {
  const leftValue = operandKey(cond.left);
  const left = operands.find((o) => o.value === leftValue);
  const shape = left?.shape || 'numeric';
  const ops = schema.operators?.[shape] || [];
  const numericOperands = operands.filter((o) => o.shape === 'numeric');

  const rightIsAttr = cond.right && cond.right.attr !== undefined;
  const needsRight = !['is_true', 'is_false'].includes(cond.op);

  const setLeft = (value) => {
    const next = operands.find((o) => o.value === value);
    const allowed = schema.operators?.[next?.shape] || [];
    const keepOp = allowed.some((o) => o.op === cond.op);
    const op = keepOp ? cond.op : allowed[0]?.op;
    onChange({
      ...cond,
      left: parseOperandKey(value),
      op,
      right: defaultRight(op, numericOperands),
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 py-1.5">
      <NativeSelect
        value={leftValue}
        onChange={(e) => setLeft(e.target.value)}
        className="h-7 w-auto min-w-[9rem] text-xs"
      >
        {!left && <option value="">pick an attribute…</option>}
        {operands.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </NativeSelect>

      <NativeSelect
        value={cond.op || ''}
        onChange={(e) => onChange({
          ...cond,
          op: e.target.value,
          right: defaultRight(e.target.value, numericOperands),
        })}
        className="h-7 w-auto min-w-[8rem] text-xs"
      >
        {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
      </NativeSelect>

      {needsRight && cond.op === 'between' && (
        <>
          <Input
            type="number" step="any" placeholder="min"
            value={cond.right?.min ?? ''}
            onChange={(e) => onChange({
              ...cond,
              right: { ...cond.right, min: Number(e.target.value) },
            })}
            className="h-7 w-20 text-xs"
          />
          <span className="text-[10px] text-muted-foreground">and</span>
          <Input
            type="number" step="any" placeholder="max"
            value={cond.right?.max ?? ''}
            onChange={(e) => onChange({
              ...cond,
              right: { ...cond.right, max: Number(e.target.value) },
            })}
            className="h-7 w-20 text-xs"
          />
        </>
      )}

      {needsRight && cond.op === 'within_bars' && (
        <Input
          type="number" step="1" min="0"
          value={cond.right?.const ?? ''}
          onChange={(e) => onChange({
            ...cond, right: { const: Number(e.target.value) },
          })}
          className="h-7 w-20 text-xs"
        />
      )}

      {needsRight && !['between', 'within_bars'].includes(cond.op) && (
        <>
          <NativeSelect
            value={rightIsAttr ? 'attr' : 'const'}
            onChange={(e) => onChange({
              ...cond,
              right: e.target.value === 'attr'
                ? { attr: numericOperands[0] ? parseOperandKey(numericOperands[0].value).attr : '', key: null, scale: 1, offset: 0 }
                : { const: 0 },
            })}
            className="h-7 w-auto text-xs"
          >
            <option value="const">a number</option>
            <option value="attr">another attribute</option>
          </NativeSelect>

          {rightIsAttr ? (
            <>
              <NativeSelect
                value={operandKey(cond.right)}
                onChange={(e) => onChange({
                  ...cond,
                  right: { ...cond.right, ...parseOperandKey(e.target.value) },
                })}
                className="h-7 w-auto min-w-[9rem] text-xs"
              >
                {numericOperands.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </NativeSelect>
              <span className="text-[10px] text-muted-foreground">×</span>
              <Input
                type="number" step="any"
                value={cond.right?.scale ?? 1}
                onChange={(e) => onChange({
                  ...cond, right: { ...cond.right, scale: Number(e.target.value) },
                })}
                className="h-7 w-16 text-xs"
                title="Multiplier — 1.5 means “one and a half times”"
              />
            </>
          ) : (
            <Input
              type="number" step="any"
              value={cond.right?.const ?? ''}
              onChange={(e) => onChange({
                ...cond, right: { const: Number(e.target.value) },
              })}
              className="h-7 w-24 text-xs"
            />
          )}
        </>
      )}

      <button
        type="button"
        onClick={onRemove}
        className="ml-auto text-muted-foreground hover:text-destructive"
        title="Remove condition"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function defaultRight(op, numericOperands) {
  if (['is_true', 'is_false'].includes(op)) return null;
  if (op === 'between') return { min: 0, max: 100 };
  if (op === 'within_bars') return { const: 5 };
  return { const: 0 };
}

function GroupCard({ group, index, operands, schema, onChange, onRemove }) {
  const update = (patch) => onChange({ ...group, ...patch });
  const setCondition = (i, cond) => update({
    conditions: group.conditions.map((c, j) => (j === i ? cond : c)),
  });

  const addCondition = () => {
    const first = operands[0];
    const shape = first?.shape || 'numeric';
    const op = (schema.operators?.[shape] || [])[0]?.op;
    update({
      conditions: [...group.conditions, {
        left: first ? parseOperandKey(first.value) : { attr: '', key: null },
        op,
        right: defaultRight(op, operands.filter((o) => o.shape === 'numeric')),
      }],
    });
  };

  return (
    <Card className="mb-3">
      <CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Input
            value={group.label || ''}
            placeholder={`Group ${index + 1}`}
            onChange={(e) => update({ label: e.target.value })}
            className="h-7 w-40 text-xs font-medium"
          />
          <span className="text-[10px] text-muted-foreground">match</span>
          <NativeSelect
            value={group.require}
            onChange={(e) => update({
              require: e.target.value,
              at_least: e.target.value === 'at_least_k'
                ? Math.min(2, group.conditions.length) : undefined,
            })}
            className="h-7 w-auto text-xs"
          >
            <option value="all">all conditions</option>
            <option value="any">any condition</option>
            <option value="at_least_k">at least…</option>
          </NativeSelect>
          {group.require === 'at_least_k' && (
            <Input
              type="number" min="1" max={group.conditions.length}
              value={group.at_least ?? 1}
              onChange={(e) => update({ at_least: Number(e.target.value) })}
              className="h-7 w-16 text-xs"
            />
          )}
          <span className="text-[10px] text-muted-foreground ml-2">weight</span>
          <Input
            type="number" step="any" min="0"
            value={group.weight ?? 1}
            onChange={(e) => update({ weight: Number(e.target.value) })}
            className="h-7 w-16 text-xs"
            title="Share of the 0–100 score this group contributes"
          />
          <label className="flex items-center gap-1 ml-2" title="Don't fail the screener when this group's data is missing — for fundamentals a company has not reported yet">
            <input
              type="checkbox"
              checked={Boolean(group.optional_if_missing)}
              onChange={(e) => update({ optional_if_missing: e.target.checked })}
            />
            <span className="text-[10px] text-muted-foreground">skip if no data</span>
          </label>
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto text-muted-foreground hover:text-destructive"
            title="Remove group"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="divide-y divide-border/40">
          {group.conditions.map((cond, i) => (
            <ConditionRow
              key={i}
              cond={cond}
              operands={operands}
              schema={schema}
              onChange={(next) => setCondition(i, next)}
              onRemove={() => update({
                conditions: group.conditions.filter((_, j) => j !== i),
              })}
            />
          ))}
        </div>

        <Button
          variant="ghost" size="sm" onClick={addCondition}
          disabled={operands.length === 0}
          className="mt-1 h-7 text-xs px-2"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />Condition
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Score ─────────────────────────────────────────────────────────────────────

function ScoreEditor({ score, operands, onChange }) {
  const numeric = operands.filter((o) => o.shape === 'numeric');
  const set = (patch) => onChange({ ...score, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Rank by</span>
      <NativeSelect
        value={score.mode}
        onChange={(e) => {
          const mode = e.target.value;
          if (mode === 'weighted_groups') onChange({ mode });
          else if (mode === 'attr') {
            onChange({
              mode,
              of: numeric[0] ? { ...parseOperandKey(numeric[0].value), scale: 1, offset: 0 } : null,
              direction: 'desc',
            });
          } else {
            onChange({
              mode,
              num: numeric[0] ? { ...parseOperandKey(numeric[0].value), scale: 1, offset: 0 } : null,
              den: numeric[1] ? { ...parseOperandKey(numeric[1].value), scale: 1, offset: 0 } : null,
            });
          }
        }}
        className="h-7 w-auto text-xs"
      >
        <option value="weighted_groups">how many conditions passed (0–100)</option>
        <option value="attr">one attribute's value</option>
        <option value="ratio_pct">how far one is above another (%)</option>
      </NativeSelect>

      {score.mode === 'attr' && (
        <>
          <NativeSelect
            value={operandKey(score.of)}
            onChange={(e) => set({ of: { ...parseOperandKey(e.target.value), scale: 1, offset: 0 } })}
            className="h-7 w-auto text-xs"
          >
            {numeric.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </NativeSelect>
          <NativeSelect
            value={score.direction || 'desc'}
            onChange={(e) => set({ direction: e.target.value })}
            className="h-7 w-auto text-xs"
          >
            <option value="desc">highest first</option>
            <option value="asc">lowest first</option>
          </NativeSelect>
        </>
      )}

      {score.mode === 'ratio_pct' && (
        <>
          <NativeSelect
            value={operandKey(score.num)}
            onChange={(e) => set({ num: { ...parseOperandKey(e.target.value), scale: 1, offset: 0 } })}
            className="h-7 w-auto text-xs"
          >
            {numeric.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </NativeSelect>
          <span className="text-[10px] text-muted-foreground">above</span>
          <NativeSelect
            value={operandKey(score.den)}
            onChange={(e) => set({ den: { ...parseOperandKey(e.target.value), scale: 1, offset: 0 } })}
            className="h-7 w-auto text-xs"
          >
            {numeric.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </NativeSelect>
        </>
      )}
    </div>
  );
}

// ── Run output ────────────────────────────────────────────────────────────────

function RunOutput({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-muted-foreground">No symbols passed in this window.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row, i) => (
        <Card key={i} className="p-0">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{row.date}</span>
              <span className="text-sm font-bold tracking-tight">
                {row.symbol.replace('.NS', '')}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1.5 font-mono text-[10px]">
              {row.score !== null && row.score !== undefined && (
                <span className="bg-muted px-1.5 py-0.5 rounded border border-border/50">
                  <span className="opacity-70 mr-1">score</span>{row.score.toFixed(2)}
                </span>
              )}
              {Object.entries(row.metadata || {}).map(([k, v]) => (
                <span key={k} className="bg-muted px-1.5 py-0.5 rounded border border-border/50">
                  <span className="opacity-70 mr-1">{k}</span>
                  {typeof v === 'number' ? v.toFixed(2) : String(v)}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SpecBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meta, setMeta] = useState(null);
  const [spec, setSpec] = useState(null);
  const [schema, setSchema] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [generated, setGenerated] = useState('');

  const [runConfig, setRunConfig] = useState({ start: DEFAULT_START, end: DEFAULT_END });
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [tab, setTab] = useState('output');

  const readOnly = meta !== null && meta.is_owner === false;

  useEffect(() => {
    api.get('/screeners').then((r) => {
      const found = r.data.find((s) => s.id === parseInt(id, 10));
      setMeta(found || null);
      // A Python screener has no spec to edit — send it to the code editor.
      if (found && found.kind === 'code') navigate(`/builder/${id}`, { replace: true });
    });
    api.get('/screeners/spec-schema').then((r) => setSchema(r.data));
    api.get('/screeners/attributes').then((r) => setCatalog(r.data));
    api.get(`/screeners/${id}/spec`)
      .then((r) => {
        setSpec(r.data.spec || emptySpec());
        setWarnings(r.data.warnings || []);
        setGenerated(r.data.generated_code || '');
        if (r.data.error) setError(r.data.error);
      })
      .catch((err) => setError(err.response?.data?.error || 'Could not load this spec'));
  }, [id, navigate]);

  const operands = useOperands(spec || emptySpec(), schema);

  const usage = useMemo(() => {
    const counts = {};
    if (!spec) return counts;
    const note = (operand) => {
      if (operand && operand.attr) counts[operand.attr] = (counts[operand.attr] || 0) + 1;
    };
    spec.groups.forEach((g) => g.conditions.forEach((c) => { note(c.left); note(c.right); }));
    if (spec.score.mode === 'attr') note(spec.score.of);
    if (spec.score.mode === 'ratio_pct') { note(spec.score.num); note(spec.score.den); }
    return counts;
  }, [spec]);

  const edit = useCallback((next) => {
    setSpec(next);
    setSaved(false);
    setError('');
  }, []);

  const addAttr = (slug, alias, params) => edit({
    ...spec,
    attrs: { ...spec.attrs, [slug]: { alias, params } },
  });

  /** Update one parameter. Nothing else — see commitParams for the name. */
  const changeParam = (slug, name, value) => edit({
    ...spec,
    attrs: {
      ...spec.attrs,
      [slug]: {
        ...spec.attrs[slug],
        params: { ...spec.attrs[slug].params, [name]: value },
      },
    },
  });

  /**
   * Resettle an attribute's name once its parameters have been edited.
   *
   * A name is suggested from the parameters when the attribute is added, so
   * SMA(period=14) arrives as `sma14`. Left alone, changing the period to 200
   * would leave `sma14` sitting next to `period 200` — and that name is not
   * cosmetic: it labels the attribute in conditions, in the chart legend, and
   * in every signal's stored metadata.
   *
   * AttrRow calls this when focus leaves the row, and only if the name was
   * still the suggested one when editing began. Doing it per keystroke would
   * change this row's React key mid-edit and tear out the focused input.
   */
  const commitParams = (slug) => {
    const decl = spec.attrs[slug];
    if (!decl) return;
    const others = Object.keys(spec.attrs).filter((s) => s !== slug);
    const settled = Object.values(decl.params).every(
      (v) => typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)),
    );
    if (!settled) return;   // a field was left empty; it will not validate anyway
    const renamed = suggestSlug(decl.alias, decl.params, others);
    if (renamed !== slug) edit(renameSlug(spec, slug, renamed));
  };

  const renameAttr = (from, to) => edit(renameSlug(spec, from, to));

  const removeAttr = (slug) => {
    const attrs = { ...spec.attrs };
    delete attrs[slug];
    // Drop anything that referenced it, so the spec stays valid.
    const groups = spec.groups.map((g) => ({
      ...g,
      conditions: g.conditions.filter(
        (c) => c.left?.attr !== slug && c.right?.attr !== slug,
      ),
    })).filter((g) => g.conditions.length > 0);
    let score = spec.score;
    const touches = [score.of, score.num, score.den].some((o) => o?.attr === slug);
    if (touches) score = { mode: 'weighted_groups' };
    edit({ ...spec, attrs, groups, score });
  };

  const addGroup = () => edit({
    ...spec,
    groups: [...spec.groups, {
      id: `group${spec.groups.length + 1}`,
      label: '',
      require: 'all',
      weight: 1,
      optional_if_missing: false,
      conditions: [],
    }],
  });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.put(`/screeners/${id}/spec`, { spec });
      setSpec(res.data.spec);
      setWarnings(res.data.warnings || []);
      setGenerated(res.data.generated_code || '');
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!saved && !readOnly) await handleSave();
    setRunning(true);
    setOutput(null);
    setTab('output');
    try {
      const res = await api.post(`/screeners/${id}/run`, runConfig);
      setOutput(res.data);
    } catch (err) {
      setOutput({ error: err.response?.data?.error || 'Run failed' });
    } finally {
      setRunning(false);
    }
  };

  const handlePromote = async () => {
    try {
      await api.post(`/screeners/${id}/promote`);
      navigate(`/builder/${id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not convert to Python');
    }
  };

  if (!spec) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {error || 'Loading…'}
      </div>
    );
  }

  const rows = output?.stdout ? safeParse(output.stdout) : null;

  return (
    <div className="flex flex-col h-screen bg-background">
      <header className="flex-none flex items-center gap-3 h-12 px-4 bg-card border-b border-border">
        <Button variant="ghost" size="sm" onClick={() => navigate('/screeners')} className="px-2">
          <span className="sm:hidden">←</span>
          <span className="hidden sm:inline">← Screeners</span>
        </Button>
        <span className="font-medium text-sm truncate max-w-[120px] sm:max-w-[200px]">
          {meta?.name || '…'}
        </span>
        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
          {readOnly ? (
            <span className="text-[10px] sm:text-xs text-muted-foreground border border-border rounded px-1.5 py-0.5">
              Read-only
            </span>
          ) : (
            <span className={`text-[10px] sm:text-xs ${saved ? 'text-win' : 'text-amber-400'}`}>
              {saved ? 'Saved' : 'Unsaved changes'}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/signals/${id}`)} className="text-xs px-2 h-8">
            Signals
          </Button>
          {!readOnly && user?.can_author_code && (
            <Button
              variant="outline" size="sm" onClick={handlePromote}
              className="text-xs px-2 h-8"
              title="Convert to a Python screener, starting from the code this spec generates. One-way."
            >
              To Python
            </Button>
          )}
          {!readOnly && (
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving || saved} className="text-xs px-2 h-8">
              {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Saving…</> : 'Save'}
            </Button>
          )}
          <Button size="sm" onClick={handleRun} disabled={running} className="text-xs px-2 h-8">
            {running ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Running…</> : '▶ Run'}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
        {/* Builder */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <p className="mb-3 rounded bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}
          {warnings.map((w) => (
            <p key={w} className="mb-3 rounded bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-xs text-amber-300 flex gap-2">
              <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{w}</span>
            </p>
          ))}

          {schema.fundamentals_enabled === false && (
            <p className="mb-3 rounded border border-border bg-muted px-3 py-2 text-[11px] text-muted-foreground">
              Fundamentals — PE, ROE, revenue growth, DVM scores — are turned
              off for now: that data is still being filled in, and screening on
              a half-populated column looks like it works while quietly missing
              companies. Price, volume, and the market-regime filters are all
              available.
            </p>
          )}

          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Attributes
            </h3>
            {/* A new tab, so an unsaved screener is still here on the way back. */}
            <Link
              to="/university"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80"
            >
              <GraduationCap className="h-3.5 w-3.5" />
              Learn about attributes
            </Link>
          </div>
          {!readOnly && (
            <div className="mb-2">
              <AddAttribute catalog={catalog} schema={schema} spec={spec} onAdd={addAttr} />
            </div>
          )}
          <Card className="mb-5">
            <CardContent className="p-0">
              {Object.keys(spec.attrs).length === 0 && (
                <p className="px-3 py-3 text-[11px] text-muted-foreground">
                  Search above to add the first one — a moving average, an
                  oscillator, a fundamental.
                </p>
              )}
              {Object.keys(spec.attrs).sort().map((slug) => (
                <AttrRow
                  key={slug}
                  slug={slug}
                  decl={spec.attrs[slug]}
                  attr={findAttr(catalog, spec.attrs[slug].alias)}
                  schema={schema}
                  usedBy={usage[slug] || 0}
                  taken={Object.keys(spec.attrs)}
                  onChangeParam={changeParam}
                  onCommitParams={commitParams}
                  onRename={renameAttr}
                  onRemove={removeAttr}
                />
              ))}
            </CardContent>
          </Card>

          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Conditions
          </h3>
          {spec.groups.map((group, i) => (
            <GroupCard
              key={group.id}
              group={group}
              index={i}
              operands={operands}
              schema={schema}
              onChange={(next) => edit({
                ...spec,
                groups: spec.groups.map((g, j) => (j === i ? next : g)),
              })}
              onRemove={() => edit({
                ...spec, groups: spec.groups.filter((_, j) => j !== i),
              })}
            />
          ))}
          {!readOnly && (
            <Button variant="outline" size="sm" onClick={addGroup} className="text-xs h-8 mb-5">
              <Plus className="h-3.5 w-3.5 mr-1" />Group
            </Button>
          )}

          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Ranking
          </h3>
          <Card>
            <CardContent className="p-3">
              <ScoreEditor
                score={spec.score}
                operands={operands}
                onChange={(score) => edit({ ...spec, score })}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right pane */}
        <div className="w-full md:max-w-[420px] flex-none flex flex-col md:border-l border-border bg-card md:overflow-hidden">
          <div className="flex-none flex border-b border-border">
            {[['output', 'Output'], ['python', 'Python']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 py-1.5 text-[11px] font-medium transition-colors ${
                  tab === key
                    ? 'text-foreground border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'output' && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 border-b border-border flex gap-3">
                <label className="flex flex-col gap-1 flex-1">
                  <span className="text-xs text-muted-foreground">From</span>
                  <Input
                    type="date" value={runConfig.start}
                    onChange={(e) => setRunConfig({ ...runConfig, start: e.target.value })}
                  />
                </label>
                <label className="flex flex-col gap-1 flex-1">
                  <span className="text-xs text-muted-foreground">To</span>
                  <Input
                    type="date" value={runConfig.end}
                    onChange={(e) => setRunConfig({ ...runConfig, end: e.target.value })}
                  />
                </label>
              </div>
              <div className="p-4 flex flex-col gap-2">
                {running && <p className="text-xs text-muted-foreground">Running…</p>}
                {output?.error && (
                  <pre className="text-xs font-mono whitespace-pre-wrap text-destructive bg-destructive/5 rounded p-2">
                    {output.error}
                  </pre>
                )}
                {output?.stderr && (
                  <pre className="text-xs font-mono whitespace-pre-wrap text-destructive bg-destructive/5 rounded p-2">
                    {output.stderr}
                  </pre>
                )}
                {rows && <RunOutput rows={rows} />}
                {!running && !output && (
                  <p className="text-xs text-muted-foreground">Press ▶ Run to try it.</p>
                )}
              </div>
            </div>
          )}

          {tab === 'python' && (
            <div className="flex-1 overflow-auto p-3">
              <p className="text-[10px] text-muted-foreground mb-2">
                What this spec compiles to. Read-only — the spec is the source of truth.
              </p>
              <pre className="text-[10px] font-mono whitespace-pre text-foreground/80">
                {generated || 'Save to see the generated code.'}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function findAttr(catalog, alias) {
  for (const cat of catalog) {
    const found = cat.attributes.find((a) => a.alias === alias);
    if (found) return found;
  }
  return null;
}

function safeParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
